/**
 * Real attestation routes.
 *
 * Server contract (see `crates/gi-api-server/src/routes/attestations.rs`):
 *
 *   GET /attestations/{id}
 *       id = hex-encoded `record_hash` (32 bytes → 64 chars).
 *       → AttestationDetail
 *       404 if no attestation with that hash.
 *
 *   GET /attestations/{id}/verify
 *       204 on verify pass; 422 on failure.
 *
 * These are NEW UI surfaces — the mock layer lists per-GI execution
 * timelines (`listAttestations(root_id)`), which is a different
 * concept (log of runs). The server's `get_attestation` resolves a
 * single engine-signed M8 record by its `record_hash`.
 *
 * Both functions are real-only. The Attestation Viewer UI currently
 * consumes the mock's `listAttestations`; when the viewer grows a
 * "lookup by record_hash" affordance it will call these.
 */

import type { AttestationDetail } from '../types';
import { ApiHttpError, apiFetch } from '../http';

export async function getAttestation(
  record_hash: string,
): Promise<AttestationDetail | null> {
  try {
    return await apiFetch<AttestationDetail>(
      `/attestations/${encodeURIComponent(record_hash)}`,
    );
  } catch (err) {
    if (err instanceof ApiHttpError && err.notFound) return null;
    throw err;
  }
}

export interface AttestationVerifyResult {
  /** `true` iff the server confirmed the signed record verifies. */
  ok: boolean;
  /** Populated on failure: the server's detail message. Empty on pass. */
  detail: string;
}

export async function verifyAttestation(
  record_hash: string,
): Promise<AttestationVerifyResult> {
  try {
    await apiFetch<void>(
      `/attestations/${encodeURIComponent(record_hash)}/verify`,
    );
    return { ok: true, detail: '' };
  } catch (err) {
    if (err instanceof ApiHttpError && err.status === 422) {
      return { ok: false, detail: err.detail ?? err.title };
    }
    throw err;
  }
}
